import { __assign, __rest } from "tslib";
import { CropRatio, FileSource, IncomingMessageType, MediaImageStudioMode, SaveStrategy, SegmentationType, } from '@wix/media-image-studio-common';
import { EventEmitter } from 'fbemitter';
import { sendBiEvent } from './bi/sendBiEvent';
import { IFRAME_STYLE } from './constants/constants';
import { MediaImageStudioURL } from './constants/urls';
import { MediaImageStudioEventTypes, } from './events.types';
import { createTransport } from './transport';
import { createIframe } from './utils/createIframe';
import { getMediaImageStudioProdUrl, getURL } from './utils/url';
export { CropRatio, MediaImageStudioMode };
export var SHOW_IFRAME_DELAY = 100;
var MediaImageStudio = /** @class */ (function () {
    function MediaImageStudio(_a) {
        var _b = _a.url, url = _b === void 0 ? getMediaImageStudioProdUrl() : _b, _c = _a.appendTo, appendTo = _c === void 0 ? document.body : _c, _d = _a.title, title = _d === void 0 ? 'Media Image Studio' : _d, _e = _a.saveStrategy, saveStrategy = _e === void 0 ? SaveStrategy.Default : _e, targetFolder = _a.targetFolder, _f = _a.segmentationType, segmentationType = _f === void 0 ? SegmentationType.Portrait : _f, siteToken = _a.siteToken, _g = _a.mediaRoot, mediaRoot = _g === void 0 ? '' : _g, _h = _a.initiator, initiator = _h === void 0 ? 'Unknown' : _h, _j = _a.metasiteId, metasiteId = _j === void 0 ? '' : _j, _k = _a.isUserLevelFile, isUserLevelFile = _k === void 0 ? false : _k, _l = _a.mode, mode = _l === void 0 ? MediaImageStudioMode.Default : _l, fileId = _a.fileId, _m = _a.fileSource, fileSource = _m === void 0 ? FileSource.Private : _m, _o = _a.defaultRatio, defaultRatio = _o === void 0 ? CropRatio.Free : _o, _p = _a.isFixedRatio, isFixedRatio = _p === void 0 ? false : _p, locale = _a.locale, crop = _a.crop, instance = _a.instance, isSiteVisitorMode = _a.isSiteVisitorMode, metaSiteToken = _a.metaSiteToken;
        var _this = this;
        this.transport = undefined;
        this.iframe = undefined;
        this.messageEmitter = new EventEmitter();
        this.requestId = 0;
        this.mediaImageStudioState = "closed" /* MediaImageStudioState.Closed */;
        this.handleConsentPolicyChanged = function () {
            var _a;
            if (!window.consentPolicyManager) {
                return;
            }
            (_a = _this.transport) === null || _a === void 0 ? void 0 : _a.sendMessage({
                type: IncomingMessageType.ConsentPolicyChanged,
                data: window.consentPolicyManager.getCurrentConsentPolicy().policy,
            });
        };
        this.iframeOptions = {
            url: url,
            appendTo: appendTo,
            title: title,
        };
        this.permanentOptions = {
            initiator: initiator,
            metasiteId: metasiteId,
            siteToken: siteToken,
            metaSiteToken: metaSiteToken,
            mediaRoot: mediaRoot,
            saveStrategy: saveStrategy,
            segmentationType: segmentationType,
            isUserLevelFile: isUserLevelFile,
            locale: locale,
            instance: instance,
            isSiteVisitorMode: isSiteVisitorMode,
            openerVersion: 'AFTER_FALCON',
        };
        this.showOptions = {
            fileId: fileId,
            fileSource: fileSource,
            mode: mode,
            isFixedRatio: isFixedRatio,
            defaultRatio: defaultRatio,
            crop: crop,
            targetFolder: targetFolder,
        };
    }
    MediaImageStudio.prototype.preload = function () {
        if (this.iframe) {
            return;
        }
        this.iframe = this.init(this.showOptions);
        this.hide();
    };
    MediaImageStudio.prototype.sendClose = function () {
        this.emitMessage({
            type: MediaImageStudioEventTypes.Close,
            payload: undefined,
        });
    };
    MediaImageStudio.prototype.hide = function () {
        if (!this.iframe) {
            return;
        }
        this.sendClose();
        window.clearTimeout(this.showTimeout);
        this.mediaImageStudioState = "closed" /* MediaImageStudioState.Closed */;
        this.iframe.style.display = 'none';
        if (this.transport) {
            this.transport.sendMessage({
                type: IncomingMessageType.IframeDisappeared,
                data: {
                    requestId: this.requestId,
                    ts: Date.now(),
                },
            });
        }
    };
    MediaImageStudio.prototype.kill = function () {
        if (!this.iframe) {
            return;
        }
        window.clearTimeout(this.showTimeout);
        var parentNode = this.iframe.parentNode;
        if (parentNode) {
            parentNode.removeChild(this.iframe);
        }
        this.iframe = undefined;
        if (this.transport) {
            this.transport.destroy();
            this.transport = undefined;
            document.removeEventListener('consentPolicyChanged', this.handleConsentPolicyChanged);
        }
        this.sendClose();
        this.messageEmitter.removeAllListeners();
    };
    /**
     * Returns true if at least one option needs to be updated
     * @param showOptions
     */
    MediaImageStudio.needsSendOptions = function (showOptions) {
        return Object.keys(showOptions).some(function (key) { return showOptions[key] !== undefined; });
    };
    /**
     * Filter out redundant options.
     * `fileId`, `fileUrl` and `isUpload` are mutually exclusive fields.
     * In case if `fileId` provided we want to skip rest two options.
     * In case `fileUrl` provided we want to skip `isUpload`.
     * @param showOptions
     */
    MediaImageStudio.prepareShowOptions = function (showOptions) {
        if (showOptions === void 0) { showOptions = {}; }
        var fileId = showOptions.fileId, fileUrl = showOptions.fileUrl, isUpload = showOptions.isUpload, restOptions = __rest(showOptions, ["fileId", "fileUrl", "isUpload"]);
        // if there is `fileId` ignore `fileUrl` and `isUpload`
        if (fileId) {
            return __assign({ fileId: fileId }, restOptions);
        }
        // if there is `fileUrl` ignore `isUpload`
        if (fileUrl) {
            return __assign({ fileUrl: fileUrl }, restOptions);
        }
        return showOptions;
    };
    MediaImageStudio.prototype.trackOpenStarted = function (options) {
        if (options === void 0) { options = {}; }
        var _a = this.permanentOptions, mediaRoot = _a.mediaRoot, openerVersion = _a.openerVersion, initiator = _a.initiator;
        sendBiEvent({
            evid: 95,
            entryPoint: options.mode || '',
            fileId: options.fileId || '',
            isSFS: Boolean(options.isUpload),
            mediaRoot: mediaRoot,
            openerVersion: openerVersion,
            initiatorName: options.initiator || initiator,
        });
    };
    MediaImageStudio.prototype.show = function (options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        if (this.mediaImageStudioState !== "closed" /* MediaImageStudioState.Closed */) {
            // start showing only when Media Image Studio closed
            return;
        }
        this.mediaImageStudioState = "opening" /* MediaImageStudioState.Opening */;
        this.trackOpenStarted(options);
        var showOptions = MediaImageStudio.prepareShowOptions(options);
        this.requestId = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
        var iframeRequestedTS = Date.now();
        var shouldSendMessage = Boolean(this.iframe) && MediaImageStudio.needsSendOptions(showOptions);
        if (!this.iframe) {
            this.iframe = this.init(showOptions);
        }
        var iframe = this.iframe;
        if (shouldSendMessage) {
            if (!this.transport) {
                throw Error('transport is not created when showing iframe');
            }
            this.transport.sendMessage({
                type: IncomingMessageType.UpdateSettings,
                data: showOptions,
            });
        }
        if (!this.transport) {
            this.transport = this.initTransport(iframe);
        }
        this.transport.sendMessage({
            type: IncomingMessageType.IframeRequested,
            data: {
                requestId: this.requestId,
                ts: iframeRequestedTS,
            },
        });
        this.transport
            .waitForClose()
            .then(function () {
            _this.hide();
        })
            .catch(function (error) {
            console.error('Error waiting for close', error);
        });
        this.transport
            .waitForImageData()
            .then(function (imageData) {
            _this.emitMessage({
                type: MediaImageStudioEventTypes.ImageData,
                payload: imageData,
            });
        })
            .catch(function (error) {
            console.error('Error waiting for image', error);
        });
        this.transport
            .waitForUploadedImage()
            .then(function (data) {
            _this.emitMessage({
                type: MediaImageStudioEventTypes.UploadedFile,
                payload: data,
            });
        })
            .catch(function (error) {
            console.error('Error waiting for upload image', error);
            _this.emitMessage({
                type: MediaImageStudioEventTypes.UploadFileFailed,
                payload: error,
            });
        });
        this.transport.waitForUploadFailed().then(function (data) {
            _this.emitMessage({
                type: MediaImageStudioEventTypes.UploadFileFailed,
                payload: data,
            });
        });
        // Set timeout to give time for post message to send and not to see changes on opened app
        this.showTimeout = window.setTimeout(function () {
            var iframeAppearedTS = Date.now();
            if (!_this.transport) {
                throw new Error('transport is not inited');
            }
            _this.transport.sendMessage({
                type: IncomingMessageType.IframeAppeared,
                data: {
                    requestId: _this.requestId,
                    ts: iframeAppearedTS,
                },
            });
            _this.mediaImageStudioState = "open" /* MediaImageStudioState.Open */;
            iframe.style.display = 'block';
            var contentWindow = iframe.contentWindow;
            if (contentWindow) {
                contentWindow.focus();
            }
        }, SHOW_IFRAME_DELAY);
    };
    MediaImageStudio.prototype.on = function (eventName, listener) {
        return this.messageEmitter.addListener(eventName, listener);
    };
    MediaImageStudio.prototype.once = function (eventName, listener) {
        return this.messageEmitter.once(eventName, listener);
    };
    MediaImageStudio.prototype.emitMessage = function (message) {
        this.messageEmitter.emit(message.type, message.payload);
    };
    MediaImageStudio.prototype.getURL = function (showOptions) {
        return getURL(this.iframeOptions.url, this.permanentOptions, showOptions);
    };
    MediaImageStudio.prototype.initTransport = function (iframe) {
        var _a = this.permanentOptions, mediaRoot = _a.mediaRoot, initiator = _a.initiator;
        var transport = createTransport(iframe, {
            debug: this.iframeOptions.url === MediaImageStudioURL.Local,
            mediaRoot: mediaRoot,
            initiator: initiator,
        });
        transport.init();
        document.addEventListener('consentPolicyChanged', this.handleConsentPolicyChanged);
        return transport;
    };
    MediaImageStudio.prototype.init = function (showOptions) {
        if (this.permanentOptions.isUserLevelFile) {
            console.warn('isUserLevelFile is deprecated. Use fileSource=FileSource.PrivateUser instead');
        }
        var iframe = createIframe();
        iframe.src = this.getURL(showOptions);
        iframe.id = 'mediaImageStudio';
        iframe.setAttribute('style', IFRAME_STYLE);
        iframe.title = this.iframeOptions.title;
        this.iframeOptions.appendTo.appendChild(iframe);
        this.transport = this.initTransport(iframe);
        return iframe;
    };
    return MediaImageStudio;
}());
export { MediaImageStudio };
//# sourceMappingURL=MediaImageStudio.js.map